import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import React from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { getBinaryRadioOptions } from 'components/status-checks/authentication-form-utils'
import { ObservabilityToolPrivateNetworkIntegration } from 'components/status-checks/form/authentication/ObservabilityToolPrivateNetworkIntegration'
import { type StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import { useObservabilityToolAuthenticationContext } from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useTranslation } from 'hooks/use-translation'

export const CustomObservabilityToolOptions: React.FC = () => {
  const { language, trans } = useTranslation()
  const { clearErrors, control, formState, register } =
    useFormContext<StatusCheckFormValues>()
  const { isEdit } = useObservabilityToolAuthenticationContext()

  const multipleRegions = useWatch<StatusCheckFormValues>({
    control,
    name: 'multipleRegions'
  })

  const binaryRadioOptions = getBinaryRadioOptions(language)

  return (
    <Stack spacing={3}>
      <FormControl>
        <Typography mb={2}>
          {trans(
            'forms.save_status_checks.custom_observability_tool_options.observability_tool_name.prompt'
          )}
        </Typography>
        <Controller
          control={control}
          name="observabilityToolNickname"
          render={({ field }) => (
            <TextField
              {...field}
              data-testid="custom-status-check-observability-tool-nickname-input"
              disabled={isEdit}
              error={Boolean(formState.errors.observabilityToolNickname)}
              helperText={formState.errors.observabilityToolNickname?.message}
              label={trans(
                'forms.save_status_checks.custom_observability_tool_options.observability_tool_name.text_field.label'
              )}
              variant="outlined"
            />
          )}
        />
      </FormControl>
      <FormControl>
        <Typography mb={1}>
          {trans(
            'forms.save_status_checks.custom_observability_tool_options.multiple_regions.prompt'
          )}
        </Typography>
        <Controller
          control={control}
          name="multipleRegions"
          render={({ field }) => (
            <RadioGroup
              {...field}
              onChange={(e) => {
                // this is necessary because radio button values are converted to strings at the DOM level
                const targetValue = e.target.value === 'true'

                // clears customDomainName field errors if multipleRegions value is false
                if (!targetValue) {
                  clearErrors('customDomainName')
                }

                field.onChange(targetValue)
              }}
            >
              {binaryRadioOptions.map(({ label, value }, index) => (
                <FormControlLabel
                  key={`${value}-${index}`}
                  control={<Radio disabled={isEdit} />}
                  label={
                    <Stack alignItems="center" direction="row" gap={3}>
                      <Typography>{label}</Typography>
                      {value && (
                        <TextField
                          disabled={!multipleRegions || isEdit}
                          error={Boolean(formState.errors.customDomainName)}
                          helperText={
                            formState.errors.customDomainName?.message
                          }
                          label={trans(
                            'forms.save_status_checks.custom_observability_tool_options.multiple_regions.custom_domain_name.text_field.label'
                          )}
                          size="small"
                          variant="outlined"
                          {...register('customDomainName')}
                        />
                      )}
                    </Stack>
                  }
                  sx={{ width: 'fit-content' }}
                  value={value}
                />
              ))}
            </RadioGroup>
          )}
        />
      </FormControl>
      <ObservabilityToolPrivateNetworkIntegration />
    </Stack>
  )
}
